import type {
  ApiErrorResponse,
  ApiResponse,
  ApiSuccess,
} from "@core/types/TApiResponse";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";

export class BaseApi {
  private axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  private extract<T>(res: ApiResponse<T>): ApiSuccess<T> {
    if (!res.success) throw res as ApiErrorResponse;

    return {
      success: true,
      data: res.data as T,
      message: res.message ?? null,
      error: null,
    };
  }

  private handleError(error: unknown): ApiErrorResponse {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as ApiResponse<unknown> | undefined;

      return {
        success: false,
        message: data?.message ?? error.message ?? "Something went wrong",
        error: {
          code: data?.error?.code ?? error.code ?? "UNKNOWN_ERROR",
          message: data?.error?.message ?? error.message ?? "Unexpected error",
          details: {
            ...(data?.error?.details ?? {}),
            status: error.response?.status,
          },
        },
        data: null,
      };
    }

    return {
      success: false,
      message: "Unexpected error",
      error: {
        code: "UNEXPECTED_ERROR",
        message: "Unexpected error occurred",
        details: {},
      },
      data: null,
    };
  }

  private async request<T>(
    fn: () => Promise<AxiosResponse<ApiResponse<T>>>,
  ): Promise<ApiSuccess<T>> {
    try {
      const response = await fn();
      return this.extract(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  get<T>(url: string, config?: AxiosRequestConfig) {
    return this.request(() =>
      this.axiosInstance.get<ApiResponse<T>>(url, config),
    );
  }

  post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    return this.request(() =>
      this.axiosInstance.post<ApiResponse<T>>(url, data, config),
    );
  }

  put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    return this.request(() =>
      this.axiosInstance.put<ApiResponse<T>>(url, data, config),
    );
  }

  patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    return this.request(() =>
      this.axiosInstance.patch<ApiResponse<T>>(url, data, config),
    );
  }

  delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.request(() =>
      this.axiosInstance.delete<ApiResponse<T>>(url, config),
    );
  }

  async getBlob(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Blob>> {
    try {
      const response = await this.axiosInstance.get<Blob>(url, {
        ...config,
        responseType: "blob",
        headers: {
          Accept: "*/*",
          ...(config?.headers ?? {}),
        },
      });

      return response; // AxiosResponse<Blob>
    } catch (error) {
      throw this.handleError(error);
    }
  }
}
